import React from 'react';
import { motion } from 'framer-motion';

const sections = [
  {
    title: 'Acceptance of Terms',
    content: [
      {
        text: 'By accessing or using AIDeoploy\'s website and services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing our services. These terms apply to all visitors, users, and clients of AIDeploy.',
      },
    ],
  },
  {
    title: 'Description of Services',
    content: [
      {
        text: 'AIDeploy provides AI consulting, implementation, and development services including but not limited to:',
        list: [
          'AI Audit Partnerships and opportunity assessments',
          'Custom AI system development and deployment',
          'AI-powered application development (VibeCode)',
          'Integration of AI solutions with existing systems',
          'Training and support for AI implementations',
        ],
      },
      {
        text: 'The specific scope, deliverables, and terms for each engagement will be outlined in a separate Statement of Work (SOW) or service agreement.',
      },
    ],
  },
  {
    title: 'Client Responsibilities',
    content: [
      {
        text: 'As a client, you agree to:',
        list: [
          'Provide accurate and complete information necessary for service delivery',
          'Grant reasonable access to systems, data, and personnel as required',
          'Review and provide timely feedback on deliverables',
          'Maintain confidentiality of any proprietary methods or tools shared',
          'Pay all fees as outlined in the applicable service agreement',
          'Comply with all applicable laws and regulations',
        ],
      },
    ],
  },
  {
    title: 'Intellectual Property',
    content: [
      {
        subtitle: 'Our Property',
        text: 'AIDeploy retains all rights to our proprietary methodologies, frameworks, tools, and pre-existing intellectual property. Any general knowledge, skills, or techniques developed or refined during an engagement remain our property.',
      },
      {
        subtitle: 'Your Property',
        text: 'You retain all rights to your pre-existing data, systems, and intellectual property. Upon full payment, you will own the custom deliverables specifically created for your engagement as outlined in the applicable SOW.',
      },
      {
        subtitle: 'License',
        text: 'Where our pre-existing tools or frameworks are incorporated into your deliverables, we grant you a non-exclusive, perpetual license to use them solely in connection with the delivered solution.',
      },
    ],
  },
  {
    title: 'Confidentiality',
    content: [
      {
        text: 'Both parties agree to maintain the confidentiality of proprietary information shared during the engagement. This includes but is not limited to:',
        list: [
          'Business strategies and plans',
          'Technical architectures and implementations',
          'Financial information and pricing',
          'Customer and employee data',
          'Trade secrets and proprietary processes',
        ],
      },
      {
        text: 'Confidentiality obligations survive termination of services for a period of three (3) years, except for trade secrets which remain protected indefinitely.',
      },
    ],
  },
  {
    title: 'Payment Terms',
    content: [
      {
        text: 'Payment terms are specified in each service agreement. General terms include:',
        list: [
          'Invoices are due within 30 days of receipt unless otherwise agreed',
          'Late payments may incur interest at 1.5% per month',
          'We reserve the right to suspend services for overdue accounts',
          'All fees are non-refundable unless otherwise specified',
          'Expenses incurred on your behalf will be billed at cost plus 10%',
        ],
      },
    ],
  },
  {
    title: 'Warranties and Disclaimers',
    content: [
      {
        text: 'We warrant that our services will be performed in a professional manner consistent with industry standards. However:',
        list: [
          'We do not guarantee specific business outcomes or ROI',
          'AI systems may produce unexpected results and require ongoing refinement',
          'Performance depends on data quality and system integration',
          'Third-party services and APIs are subject to their own terms',
        ],
      },
      {
        text: 'EXCEPT AS EXPRESSLY STATED, SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE.',
      },
    ],
  },
  {
    title: 'Limitation of Liability',
    content: [
      {
        text: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW, AIDEPLOY\'S TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM OR RELATED TO OUR SERVICES SHALL NOT EXCEED THE TOTAL FEES PAID BY YOU IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.',
      },
      {
        text: 'WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES.',
      },
    ],
  },
  {
    title: 'Indemnification',
    content: [
      {
        text: 'You agree to indemnify and hold harmless AIDeploy and its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorney fees) arising from:',
        list: [
          'Your use of our services',
          'Your violation of these terms',
          'Your violation of any third-party rights',
          'Your data or content provided to us',
        ],
      },
    ],
  },
  {
    title: 'Term and Termination',
    content: [
      {
        text: 'These terms remain in effect while you use our services. Either party may terminate an engagement:',
        list: [
          'For convenience with 30 days written notice',
          'Immediately for material breach not cured within 15 days of notice',
          'Immediately if the other party becomes insolvent',
        ],
      },
      {
        text: 'Upon termination, you must pay for all services rendered through the termination date. Sections on confidentiality, intellectual property, limitation of liability, and indemnification survive termination.',
      },
    ],
  },
  {
    title: 'Dispute Resolution',
    content: [
      {
        text: 'Any disputes arising from these terms or our services will be resolved as follows:',
        list: [
          'First, through good-faith negotiation between the parties',
          'If unresolved within 30 days, through binding arbitration',
          'Arbitration will be conducted in accordance with AAA rules',
          'The arbitration will take place in [Your State/Country]',
          'Each party bears its own costs; arbitration fees are split equally',
        ],
      },
    ],
  },
  {
    title: 'General Provisions',
    content: [
      {
        text: 'Additional terms governing our relationship:',
        list: [
          'These terms constitute the entire agreement between the parties',
          'Amendments must be in writing and signed by both parties',
          'Our failure to enforce any provision does not waive our rights',
          'If any provision is unenforceable, others remain in effect',
          'You may not assign these terms without our written consent',
          'These terms are governed by the laws of [Your State/Country]',
        ],
      },
    ],
  },
  {
    title: 'Contact Information',
    content: [
      {
        text: 'For questions about these Terms of Service, please contact us:',
      },
    ],
  },
];

export const TermsOfServicePage: React.FC = () => {
  return (
    <main className="bg-[#0a0a0a]">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center overflow-hidden">
        {/* Animated background blobs */}
        <motion.div
          className="absolute top-1/4 right-1/3 w-[500px] h-[500px] bg-violet-500/5 rounded-full blur-[150px]"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        <div className="relative w-full max-w-4xl mx-auto px-6 lg:px-8 py-32">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-zinc-800/50 border border-zinc-700/50 backdrop-blur-sm"
          >
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-violet-400"
              animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="text-zinc-300 text-sm font-medium">Legal</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-[1.1]"
          >
            Terms of Service
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="text-xl text-zinc-400 leading-relaxed mb-4"
          >
            The legal agreement between you and AIDeploy. Please read these terms carefully before using our services.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
            className="text-zinc-500 text-sm"
          >
            Last Updated: February 2026
          </motion.p>
        </div>

        {/* Bottom gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
      </section>

      {/* Content Section */}
      <section className="relative py-16 overflow-hidden">
        {/* Top border */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
          {/* Quick Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800/50 backdrop-blur-sm"
          >
            <h2 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">
              Quick Navigation
            </h2>
            <div className="flex flex-wrap gap-2">
              {sections.slice(0, 8).map((section, index) => (
                <a
                  key={index}
                  href={`#section-${index}`}
                  className="px-3 py-1.5 text-sm text-zinc-500 hover:text-white bg-zinc-800/50 hover:bg-zinc-800 rounded-lg transition-colors"
                >
                  {section.title}
                </a>
              ))}
              <span className="px-3 py-1.5 text-sm text-zinc-600">+{sections.length - 8} more</span>
            </div>
          </motion.div>

          <div className="space-y-12">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                id={`section-${index}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: index * 0.03, ease: "easeOut" }}
                className="relative scroll-mt-24"
              >
                <div className="flex items-start gap-4">
                  <motion.div
                    className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500/20 to-blue-500/20 border border-violet-500/30 flex items-center justify-center mt-1"
                    whileHover={{ scale: 1.1 }}
                  >
                    <span className="text-violet-400 text-sm font-semibold">{index + 1}</span>
                  </motion.div>
                  <div className="flex-1">
                    <h2 className="text-xl md:text-2xl font-bold text-white mb-4">
                      {section.title}
                    </h2>
                    <div className="space-y-4">
                      {section.content.map((item, itemIndex) => (
                        <div key={itemIndex}>
                          {'subtitle' in item && item.subtitle && (
                            <h3 className="text-lg font-semibold text-zinc-300 mb-2">
                              {item.subtitle}
                            </h3>
                          )}
                          {item.text && (
                            <p className={`text-zinc-400 leading-relaxed ${item.text === item.text.toUpperCase() ? 'text-sm font-medium' : ''}`}>
                              {item.text}
                            </p>
                          )}
                          {'list' in item && item.list && (
                            <ul className="mt-3 space-y-2">
                              {item.list.map((listItem: string, listIndex: number) => (
                                <motion.li
                                  key={listIndex}
                                  initial={{ opacity: 0, x: -10 }}
                                  whileInView={{ opacity: 1, x: 0 }}
                                  viewport={{ once: true }}
                                  transition={{ delay: listIndex * 0.03 }}
                                  className="flex items-start gap-3 text-zinc-400"
                                >
                                  <svg className="w-5 h-5 text-violet-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                  <span>{listItem}</span>
                                </motion.li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Divider */}
                {index < sections.length - 1 && (
                  <div className="mt-12 h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />
                )}
              </motion.div>
            ))}

            {/* Contact Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative mt-8"
            >
              <div className="absolute -inset-[1px] bg-gradient-to-r from-violet-500/30 via-blue-500/20 to-violet-500/30 rounded-2xl" />
              <div className="relative bg-zinc-900/90 backdrop-blur-sm rounded-2xl p-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      Questions about these terms?
                    </h3>
                    <p className="text-zinc-400">
                      Email us at{' '}
                      <a href="mailto:legal@aideploy.com" className="text-violet-400 hover:text-violet-300 transition-colors">
                        legal@aideploy.com
                      </a>
                    </p>
                  </div>
                  <motion.a
                    href="/contact"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white text-zinc-900 font-semibold rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-white/10"
                    whileHover={{ y: -2, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Contact Us
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </motion.a>
                </div>
              </div>
            </motion.div>

            {/* Related Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-8"
            >
              <a
                href="/privacy"
                className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Privacy Policy
              </a>
              <span className="hidden sm:block text-zinc-700">|</span>
              <motion.a
                href="/"
                className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors"
                whileHover={{ x: -5 }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                </svg>
                Back to Home
              </motion.a>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default TermsOfServicePage;
